import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const VatRatesTab = () => {
  const [vatRates, setVatRates] = useState([]);
  const [newVatRate, setNewVatRate] = useState({ name: '', rate: '', description: '' });
  const [editingVatRate, setEditingVatRate] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadVatRates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/vat-rates');
      if (data.success) {
        setVatRates(data.data?.rates || []);
      }
    } catch (err) {
      console.error('Błąd podczas pobierania stawek VAT:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVatRates();
  }, [loadVatRates]);

  const addVatRate = async () => {
    if (!newVatRate.name.trim() || newVatRate.rate === '') {
      toast.error('Nazwa i stawka VAT są wymagane');
      return;
    }

    try {
      const { data } = await api.post('/admin/vat-rates', newVatRate);
      if (data.success) {
        setNewVatRate({ name: '', rate: '', description: '' });
        loadVatRates();
        toast.success('Stawka VAT została dodana');
      } else {
        toast.error(data.message || 'Błąd dodawania stawki VAT');
      }
    } catch (err) {
      console.error('Błąd podczas dodawania stawki VAT:', err);
      toast.error('Błąd podczas dodawania stawki VAT');
    }
  };

  const deleteVatRate = async (rateId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę stawkę VAT?')) {
      return;
    }

    try {
      const { data } = await api.delete(`/admin/vat-rates/${rateId}`);
      if (data.success) {
        loadVatRates();
        toast.success(data.message || 'Stawka VAT została usunięta');
      } else {
        toast.error(data.message || 'Błąd usuwania stawki VAT');
      }
    } catch (err) {
      console.error('Błąd podczas usuwania stawki VAT:', err);
      toast.error('Błąd podczas usuwania stawki VAT');
    }
  };

  const startEditVatRate = (rate) => {
    setEditingVatRate({
      id: rate.id,
      name: rate.name,
      rate: rate.rate,
      description: rate.description || ''
    });
  };

  const cancelEditVatRate = () => {
    setEditingVatRate(null);
  };

  const updateVatRate = async () => {
    if (!editingVatRate?.name?.trim() || editingVatRate?.rate === '') {
      toast.error('Nazwa i stawka VAT są wymagane');
      return;
    }

    try {
      const { data } = await api.put(`/admin/vat-rates/${editingVatRate.id}`, {
        name: editingVatRate.name,
        rate: editingVatRate.rate,
        description: editingVatRate.description
      });
      if (data.success) {
        loadVatRates();
        setEditingVatRate(null);
        toast.success('Stawka VAT została zaktualizowana');
      } else {
        toast.error(data.message || 'Błąd aktualizacji stawki VAT');
      }
    } catch (err) {
      console.error('Błąd podczas aktualizacji stawki VAT:', err);
      toast.error('Błąd podczas aktualizacji stawki VAT');
    }
  };

  return (
    <div className="admin-table-wrap" style={{ maxWidth: '600px' }}>
      <style>{`
        @media (max-width: 600px) {
          .vat-form-grid { grid-template-columns: 1fr !important; }
          .vat-edit-row { flex-wrap: wrap !important; }
          .vat-edit-row input { flex: 1 1 100px !important; min-width: 0 !important; width: auto !important; }
          .vat-list-row { flex-direction: column !important; align-items: stretch !important; gap: 0.4rem !important; }
        }
        @media (min-width: 601px) and (max-width: 900px) {
          .vat-form-grid { grid-template-columns: 1fr 80px 1fr !important; }
        }
      `}</style>
      {/* Formularz dodawania */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '5px',
        marginBottom: '1rem',
        padding: '0.75rem',
        background: 'var(--bg)'
      }}>
        <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
          {'➕'} Nowa Stawka VAT
        </h6>
        <div className="vat-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr auto', gap: '0.5rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Nazwa *
            </label>
            <input
              type="text"
              value={newVatRate.name}
              onChange={(e) => setNewVatRate({ ...newVatRate, name: e.target.value })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', height: '28px' }}
              placeholder="np. VAT 7%"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Stawka % *
            </label>
            <input
              type="number"
              value={newVatRate.rate}
              onChange={(e) => setNewVatRate({ ...newVatRate, rate: e.target.value })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', height: '28px' }}
              placeholder="7"
              min="0" max="100" step="0.01"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Opis
            </label>
            <input
              type="text"
              value={newVatRate.description}
              onChange={(e) => setNewVatRate({ ...newVatRate, description: e.target.value })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', height: '28px' }}
              placeholder="Opcjonalnie"
            />
          </div>
          <button
            onClick={addVatRate}
            style={{ padding: '4px 10px', border: '1px solid var(--success)', color: 'var(--success)', fontSize: '11px', fontWeight: '500', height: '28px' }}
          >
            Dodaj
          </button>
        </div>
      </div>

      {/* Lista */}
      <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{ backgroundColor: 'var(--border)', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>{'💰'}</span>
          <h6 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>Lista Stawek VAT ({vatRates.length})</h6>
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {vatRates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <p style={{ color: 'var(--text-muted)', margin: '0 0 0.5rem 0', fontSize: '12px' }}>Brak stawek VAT</p>
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post('/admin/init-vat-rates');
                    if (data.success) {
                      loadVatRates();
                      toast.success('Dodano domyślne polskie stawki VAT');
                    } else {
                      toast.error(data.message || 'Błąd');
                    }
                  } catch (err) {
                    toast.error('Błąd podczas inicjalizacji stawek VAT');
                  }
                }}
                style={{ padding: '6px 12px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' }}
              >
                {'➕'} Dodaj domyślne polskie stawki VAT
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {vatRates.map((rate) => (
                <div
                  key={rate.id}
                  className="vat-list-row"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--card-bg)',
                    borderBottom: '1px solid var(--bg)',
                    fontSize: '12px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {editingVatRate && editingVatRate.id === rate.id ? (
                      <div className="vat-edit-row" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input type="text" value={editingVatRate?.name || ''} onChange={(e) => setEditingVatRate({ ...editingVatRate, name: e.target.value })}
                          style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px', width: '120px' }} placeholder="Nazwa" />
                        <input type="number" value={editingVatRate?.rate || ''} onChange={(e) => setEditingVatRate({ ...editingVatRate, rate: e.target.value })}
                          style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px', width: '60px' }} placeholder="%" min="0" max="100" step="0.01" />
                        <input type="text" value={editingVatRate?.description || ''} onChange={(e) => setEditingVatRate({ ...editingVatRate, description: e.target.value })}
                          style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px', width: '150px' }} placeholder="Opis" />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{rate.name}</span>
                        <span style={{ fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)', padding: '1px 8px', borderRadius: '3px', fontWeight: '600' }}>{rate.rate}%</span>
                        {rate.description && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>- {rate.description}</span>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {editingVatRate && editingVatRate.id === rate.id ? (
                      <>
                        <button onClick={updateVatRate} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' }}>Zapisz</button>
                        <button onClick={cancelEditVatRate} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}>Anuluj</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEditVatRate(rate)} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' }}>Edytuj</button>
                        <button onClick={() => deleteVatRate(rate.id)} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' }}>Usuń</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VatRatesTab;
